import { useState, useEffect, useCallback } from "react"
import { fboApi } from "@/api/fbo-api.client"
import type { GetToken } from "@/api/ats-sync-api.client"
import type { TemplateDto } from "@/api/fbo-api.types"
import type { TemplateV2 } from "@/components/ats-fbo/types"
import { T4_TYPE } from "@/components/ats-fbo/constants"
import { env } from "@/lib/env"
import { logger } from "@/lib/logger"
import { templatesV2 as mockTemplates } from "@/components/ats-fbo/mock-data"

export interface TemplateOption {
  id: string
  name: string
}

// TODO: Move template filtering to the FBO API to avoid duplicating appNext logic

export function useTemplates(getToken: GetToken) {
  const useMock = env.useMockData || !env.fboApiUrl
  const [apiTemplates, setApiTemplates] = useState<TemplateDto[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (useMock) return
    setLoading(true)
    fboApi
      .getTemplates(getToken)
      .then(setApiTemplates)
      .catch((err: unknown) => { logger.error(err); setApiTemplates([]) })
      .finally(() => setLoading(false))
  }, [useMock, getToken])

  const getFilteredTemplates = useCallback(
    (
      clientOrgId: string | null,
      lobId: number | null,
      lobCode: string | null,
      t4TypeId: number | null
    ): TemplateOption[] => {
      // Mock mode: filter TemplateV2[] by both lobCode and clientOrgId
      if (useMock) {
        if (!lobCode || !clientOrgId) return []
        return mockTemplates
          .filter((t: TemplateV2) => t.lobCode === lobCode && t.clientOrgId === clientOrgId)
          .map((t: TemplateV2) => ({ id: t.id, name: t.name }))
      }

      // API mode: filter TemplateDto[] by nested structure
      const clientOrgIdNum = Number(clientOrgId)
      if (!clientOrgIdNum || !lobId) return []

      const isT4Salaried = t4TypeId === T4_TYPE.T4Salaried
      const result: TemplateOption[] = []

      for (const tpl of apiTemplates) {
        // Filter by T4 Salaried status
        if (isT4Salaried) {
          if (tpl.Entity?.IsT4SalariedBillSideWorkOrder !== true) continue
        } else {
          if (tpl.Entity?.IsT4SalariedBillSideWorkOrder === true) continue
        }

        // Match LOB and Client Org within nested work order structure
        let matched = false
        for (const wo of tpl.Entity?.WorkOrders ?? []) {
          if (matched) break
          for (const wov of wo.WorkOrderVersions ?? []) {
            if (matched) break
            if (wov.LineOfBusinessId !== lobId) continue
            for (const bi of wov.BillingInfoes ?? []) {
              if (bi.OrganizationIdClient === clientOrgIdNum) {
                matched = true
                break
              }
            }
          }
        }

        if (matched) {
          const displayName = tpl.Description
            ? `${tpl.Name} (${tpl.Description})`
            : tpl.Name
          result.push({ id: String(tpl.Id), name: displayName })
        }
      }

      return result
    },
    [useMock, apiTemplates]
  )

  return { loading, getFilteredTemplates }
}
